import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { payslipService, type Payslip } from "@/services/payslipService"
import { FileText, Download, RefreshCw, Calendar, User, Search, Filter, ChevronDown } from "lucide-react"
import toast from "react-hot-toast"

const AllPayslips: React.FC = () => {
  const [isDownloading, setIsDownloading] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [monthFilter, setMonthFilter] = useState<string>("all")
  const [yearFilter, setYearFilter] = useState<string>("all")

  // Fetch all payslips (admin/HR endpoint)
  const { data: payslipsData, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["allPayslips"],
    queryFn: () => payslipService.getAllPayslips({ page: 1, limit: 50 }),
    staleTime: 5 * 60 * 1000,
  })

  const allPayslips = payslipsData?.data?.payslips || []
  const totalPayslips = allPayslips.length

  // Helper function to get employee name safely
  const getEmployeeName = (payslip: Payslip): string => {
    if (payslip.employee && payslip.employee.firstName && payslip.employee.lastName) {
      return `${payslip.employee.firstName} ${payslip.employee.lastName}`
    }
    return "Unknown Employee"
  }

  // Helper function to get employee number safely
  const getEmployeeNumber = (payslip: Payslip): string => {
    return payslip.employee?.employeeNumber || "No ID"
  }

  // Helper function to get employee ID safely
  const getEmployeeId = (payslip: Payslip): number | null => {
    return payslip.employee?.id || null
  }

  // Filtrer les payslips
  const filteredPayslips = allPayslips.filter((payslip: Payslip) => {
    const employeeName = getEmployeeName(payslip).toLowerCase()
    const employeeNumber = getEmployeeNumber(payslip).toLowerCase()

    const matchesSearch =
      employeeName.includes(searchTerm.toLowerCase()) ||
      employeeNumber.includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === "all" || payslip.payrollRun.status === statusFilter
    const matchesMonth = monthFilter === "all" || payslip.payrollRun.payrollMonth.toString() === monthFilter
    const matchesYear = yearFilter === "all" || payslip.payrollRun.payrollYear.toString() === yearFilter

    return matchesSearch && matchesStatus && matchesMonth && matchesYear
  })

  // Obtenir les années et mois uniques pour les filtres
  const uniqueYears = Array.from(new Set(allPayslips.map((p: Payslip) => p.payrollRun.payrollYear))).sort((a, b) => b - a)
  const uniqueMonths = Array.from(new Set(allPayslips.map((p: Payslip) => p.payrollRun.payrollMonth))).sort((a, b) => a - b)

  // Compter les employés uniques
  const uniqueEmployeeCount = Array.from(new Set(allPayslips.map(p => getEmployeeId(p)))).filter(id => id !== null).length

  const handleDownloadPayslip = async (payslipId: number, employeeName: string, month: string, year: number) => {
    setIsDownloading(payslipId)
    try {
      const blob = await payslipService.downloadPayslipPDF(payslipId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `${employeeName.replace(/\s+/g, '_')}_Payslip_${month}_${year}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success("Payslip downloaded successfully!")
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || "Failed to download payslip")
    } finally {
      setIsDownloading(null)
    }
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })

  const getMonthName = (month: number) =>
    [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ][month - 1] || "Unknown"

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "finalized":
        return "bg-emerald-50 text-emerald-700 border-emerald-200"
      case "processing":
        return "bg-amber-50 text-amber-700 border-amber-200"
      case "draft":
      default:
        return "bg-indigo-50 text-indigo-700 border-indigo-200"
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <FileText size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              All Employee Payslips
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              View and download payslips for all employees
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Total Payslips</div>
            <div className="text-gray-900 dark:text-white font-medium text-xs flex items-center gap-1">
              <FileText size={12} />
              {totalPayslips}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Employees</div>
            <div className="text-gray-900 dark:text-white font-medium text-xs flex items-center gap-1">
              <User size={12} />
              {uniqueEmployeeCount}
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="processing">Processing</option>
                <option value="finalized">Finalized</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>

            {/* Month Filter */}
            <div className="relative">
              <select
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="all">All Months</option>
                {uniqueMonths.map(month => (
                  <option key={month} value={month.toString()}>
                    {getMonthName(month)}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>

            {/* Year Filter */}
            <div className="relative">
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="all">All Years</option>
                {uniqueYears.map(year => (
                  <option key={year} value={year.toString()}>{year}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isRefetching}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 hover:shadow-md bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <RefreshCw size={14} className={isRefetching ? "animate-spin" : ""} />
              {isRefetching ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredPayslips.length} of {totalPayslips} payslips
        </p>
      </div>

      {/* Payslips Table */}
      {isLoading ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-8 text-center">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400 text-sm">Loading payslips...</p>
        </div>
      ) : filteredPayslips.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-8 text-center">
          <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3">
            <FileText className="text-gray-400 dark:text-gray-500 h-5 w-5" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No Payslips Found</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            {allPayslips.length === 0 ? "No payslips have been generated yet." : "No payslips match your filters."}
          </p>
          {allPayslips.length > 0 && (
            <button
              onClick={() => {
                setSearchTerm("")
                setStatusFilter("all")
                setMonthFilter("all")
                setYearFilter("all")
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Filter size={14} />
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Employee</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Pay Period</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Date Range</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Net Pay</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Status</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredPayslips.map((payslip: Payslip) => {
                  const statusClasses = getStatusBadge(payslip.payrollRun.status)
                  const employeeName = getEmployeeName(payslip)
                  const employeeNumber = getEmployeeNumber(payslip)

                  return (
                    <tr key={payslip.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600">
                            <User size={14} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                              {employeeName}
                            </h4>
                            <p className="text-gray-500 dark:text-gray-400 text-xs">
                              {employeeNumber}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600">
                            <Calendar size={14} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                              {getMonthName(payslip.payrollRun.payrollMonth)} {payslip.payrollRun.payrollYear}
                            </h4>
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Payroll Cycle</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                          <span className="text-xs">
                            {formatDate(payslip.payrollRun.periodStart)} – {formatDate(payslip.payrollRun.periodEnd)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-gray-900 dark:text-white text-sm">
                          {formatCurrency(payslip.netPay, payslip.salaryCurrency)}
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">
                          {payslip.salaryCurrency}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${statusClasses}`}
                        >
                          {payslip.payrollRun.status.charAt(0).toUpperCase() + payslip.payrollRun.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* <button
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <Eye size={12} />
                            View
                          </button> */}
                          <button
                            onClick={() =>
                              handleDownloadPayslip(
                                payslip.id,
                                employeeName,
                                getMonthName(payslip.payrollRun.payrollMonth),
                                payslip.payrollRun.payrollYear
                              )
                            }
                            disabled={isDownloading === payslip.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-sm disabled:opacity-50 bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            {isDownloading === payslip.id ? (
                              <>
                                <RefreshCw size={12} className="animate-spin" />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <Download size={12} />
                                Download PDF
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default AllPayslips
